import { isNil, either, reject } from 'ramda';

// Helper functions to extract primary contact information

/**
 * Extracts the primary phone number from a phones array, with fallback support
 * @param {Array} phones - Array of phone objects with phoneNumber and isPrimary properties
 * @param {string} fallbackPhone - Fallback phone number if no primary found
 * @returns {string|null} The primary phone number or fallback, or null if none available
 */
export const getPrimaryPhoneNumber = (phones, fallbackPhone) => {
  if (!phones || phones.length === 0) {
    return null; // Return null when array is empty
  }

  const primaryPhone = phones.find((phone) => phone.isPrimary);
  const selectedPhone = primaryPhone || phones[0]; // If no primary found, use first item

  if (selectedPhone && selectedPhone.phoneNumber) {
    return selectedPhone.phoneNumber;
  }

  return fallbackPhone || null;
};

/**
 * Extracts the primary email address from an emails array, with fallback support
 * @param {Array} emails - Array of email objects with emailAddress and isPrimary properties
 * @param {string} fallbackEmail - Fallback email address if no primary found
 * @returns {string|null} The primary email address or fallback, or null if none available
 */
export const getPrimaryEmailAddress = (emails, fallbackEmail) => {
  if (!emails || emails.length === 0) {
    return null; // Return null when array is empty
  }

  const primaryEmail = emails.find((email) => email.isPrimary);
  const selectedEmail = primaryEmail || emails[0]; // If no primary found, use first item

  if (selectedEmail && selectedEmail.emailAddress) {
    return selectedEmail.emailAddress;
  }

  return fallbackEmail || null;
};

/**
 * Helper function to process contact arrays (emails or phones) in mixed mode
 */
export const processContactArray = (
  contacts,
  legacyValue,
  contactKey,
  usePrimaryMapping,
  isNilOrDefault,
) => {
  if (!contacts?.length) {
    return legacyValue && !isNilOrDefault(legacyValue)
      ? { [contactKey]: legacyValue }
      : {};
  }

  // Removing duplicates based on contactKey
  const uniqueContacts = contacts.reduce((acc, contact) => {
    const contactValue = contact[contactKey];
    if (
      contactValue &&
      !acc.some((existing) => existing[contactKey] === contactValue)
    ) {
      acc.push(contact);
    }
    return acc;
  }, []);

  // Keep only the first primary contact
  let foundPrimary = false;
  const contactsCopy = uniqueContacts.map((contact) => {
    if (contact.isPrimary) {
      if (foundPrimary) {
        return { ...contact, isPrimary: false };
      }

      foundPrimary = true;
      return contact;
    }
    return contact;
  });
  const primaryIndex = contactsCopy.findIndex((contact) => contact.isPrimary);
  const result = {};

  // Set legacy field value
  if (legacyValue && !isNilOrDefault(legacyValue)) {
    // Update primary contact to match legacy field if it exists
    if (primaryIndex !== -1) {
      contactsCopy[primaryIndex] = {
        ...contactsCopy[primaryIndex],
        [contactKey]: legacyValue,
      };
    }
    result[contactKey] = legacyValue;
  } else if (primaryIndex !== -1) {
    // Use primary contact value for legacy field
    result[contactKey] = contactsCopy[primaryIndex][contactKey];
  }

  // Process contact array
  const arrayKey = contactKey === 'emailAddress' ? 'emails' : 'phones';
  result[arrayKey] = contactsCopy
    .filter((contact) => contact[contactKey])
    .map((contact) => ({
      ...contact,
      isPrimary: usePrimaryMapping
        ? contact[contactKey] === result[contactKey]
        : contact.isPrimary,
    }));

  return result;
};

/**
 * Transforms contact information for submission, handling backwards compatibility
 * between legacy single-value fields and new array-based structures
 * @param {Object} contactInfo - Contact information object that may contain legacy fields and/or arrays
 * @param {Object} options - Configuration options
 * @param {boolean} options.includeContactId - Whether to include contactId in the result (default: true)
 * @param {boolean} options.usePrimaryMapping - Whether to map primary entries back to legacy fields (default: true)
 * @returns {Object} Transformed contact information ready for submission
 */
export const transformContactInformationForSubmission = (
  contactInfo,
  options = {},
) => {
  if (!contactInfo) return {};

  const {
    includeContactId = true,
    usePrimaryMapping = true,
    applicationAdditionalPhoneEmailAndTypes = false,
  } = options;
  const isDefault = (val) => val === 'default' || val === '';
  const isNilOrDefault = either(isNil, isDefault);

  const hasArrays =
    contactInfo.emails?.length > 0 || contactInfo.phones?.length > 0;

  if (!applicationAdditionalPhoneEmailAndTypes) {
    // Mixed mode - sync legacy fields with primary entries in arrays
    const emailResult = processContactArray(
      contactInfo.emails,
      contactInfo.emailAddress,
      'emailAddress',
      usePrimaryMapping,
      isNilOrDefault,
    );

    const phoneResult = processContactArray(
      contactInfo.phones,
      contactInfo.phoneNumber,
      'phoneNumber',
      usePrimaryMapping,
      isNilOrDefault,
    );

    return { ...emailResult, ...phoneResult };
  }

  if (hasArrays) {
    // New array-based structure only
    const result = {};

    // Extract primary contact for backward compatibility
    const primaryEmail = contactInfo.emails?.find((email) => email.isPrimary);
    const primaryPhone = contactInfo.phones?.find((phone) => phone.isPrimary);

    if (primaryEmail?.emailAddress) {
      result.emailAddress = primaryEmail.emailAddress;
    }
    if (primaryPhone?.phoneNumber) {
      result.phoneNumber = primaryPhone.phoneNumber;
    }

    // Include full arrays for new API endpoints
    if (contactInfo.emails?.length > 0) {
      result.emails = contactInfo.emails.filter((email) => email.emailAddress);
    }
    if (contactInfo.phones?.length > 0) {
      result.phones = contactInfo.phones.filter((phone) => phone.phoneNumber);
    }

    if (includeContactId && contactInfo.contactId) {
      result.contactId = contactInfo.contactId;
    }
    return result;
  }

  // Legacy structure only
  return reject(isNilOrDefault, contactInfo);
};
